/* @HEADER
 * @version 1.1.1375 | 2026-10-31
 * @purpose ZVX-DEV-8226 — the store that holds the notice, and the banner «Project "X" was deleted» (ru/en) shown when the project the person had open was deleted on another device; held by the host so it survives the app's remount, dismissed by the person.
 * @sidecar ProjectDeletedBanner.tsx.header.md
 * @layer app | @hex _none_ | @ctx project
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8226

import { useSyncExternalStore } from 'react'
import { useMessages } from '../shared/i18n/useMessages'
import { PROJECT_SWITCHER_TESTIDS } from './project-switcher.testids'
import { leftOrganisationsSentence } from './leftOrganisationsSentence'
import './ProjectDeletedBanner.css'

export interface ProjectDeletedNotice {
  /**
   * ZVX-DEV-8269 -- why the window moved. Absent: the project was deleted on another device. `left-organisation`: the person was
   * removed from the organisation and its projects are no longer shown; only `organisationName` is read then.
   */
  reason?: 'left-organisation'
  /** The deleted project's name, or null when nothing on this device knew it. */
  removedName: string | null
  /** The project the app moved to, or null when the organisation has none left. */
  openedName: string | null
  /** The organisation's name, for the «nothing left» wording. */
  organisationName: string | null
  /**
   * ZVX-5305 -- `left-organisation` only: the names of EVERY organisation the person was removed from, the one the window stood in
   * first. Absent: the single `organisationName` is the whole list.
   */
  organisationNames?: readonly string[]
  /** `left-organisation` only: how many of them had no name on this device, said as «и ещё N». */
  unnamedCount?: number
}

/**
 * The notice «the project you had open was deleted», as an object made by whoever owns the window's life (the host) and handed
 * to what sets it and to what shows it. The module holds nothing. A later notice replaces the earlier one: there is one banner,
 * about the latest.
 */
export interface ProjectDeletedNoticeStore {
  set(notice: ProjectDeletedNotice): void
  clear(): void
  get(): ProjectDeletedNotice | null
  subscribe(listener: () => void): () => void
}

export function createProjectDeletedNotice(): ProjectDeletedNoticeStore {
  let current: ProjectDeletedNotice | null = null
  const listeners = new Set<() => void>()
  const emit = () => {
    for (const listener of [...listeners]) listener()
  }
  return {
    set(notice) {
      current = notice
      emit()
    },
    clear() {
      if (current === null) return
      current = null
      emit()
    },
    get: () => current,
    subscribe(listener) {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}

interface ProjectDeletedBannerProps {
  /** The host's store: the banner reads it and clears it, and holds nothing itself. */
  store: ProjectDeletedNoticeStore
}

/** Renders nothing until a notice is set; stays until «Got it» (no timer), and is not kept over a reload. */
export function ProjectDeletedBanner({ store }: ProjectDeletedBannerProps) {
  const m = useMessages()
  const notice = useSyncExternalStore(store.subscribe, store.get, store.get)
  if (notice === null) return null
  if (notice.reason === 'left-organisation') {
    const sentence = leftOrganisationsSentence(m.projectSwitcher.leftOrganisation, notice)
    return (
      <div
        className="project-deleted-banner"
        data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereBanner}
        role="status"
        aria-live="polite"
      >
        <div className="project-deleted-banner__text">
          <strong data-testid={PROJECT_SWITCHER_TESTIDS.leftOrganisationTitle}>{sentence.title}</strong>
          <span data-testid={PROJECT_SWITCHER_TESTIDS.leftOrganisationBody}>{sentence.body}</span>
        </div>
        <button
          type="button"
          className="project-deleted-banner__dismiss"
          data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereDismiss}
          onClick={store.clear}
        >
          {m.projectSwitcher.deletedElsewhere.dismiss}
        </button>
      </div>
    )
  }
  const text = m.projectSwitcher.deletedElsewhere
  return (
    <div
      className="project-deleted-banner"
      data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereBanner}
      role="status"
      aria-live="polite"
    >
      <div className="project-deleted-banner__text">
        <strong data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereTitle}>
          {notice.removedName ? text.title(notice.removedName) : text.titleUnnamed}
        </strong>
        {notice.openedName !== null ? (
          <span data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereOpened}>{text.opened(notice.openedName)}</span>
        ) : (
          <span data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereNoneLeft}>
            {notice.organisationName ? text.noneLeft(notice.organisationName) : text.noneLeftUnnamed}
          </span>
        )}
      </div>
      <button
        type="button"
        className="project-deleted-banner__dismiss"
        data-testid={PROJECT_SWITCHER_TESTIDS.deletedElsewhereDismiss}
        onClick={store.clear}
      >
        {text.dismiss}
      </button>
    </div>
  )
}
